"use client";

import type { RefObject } from "react";
import {
  clamp,
  DiagramFrame,
  Guide,
  Label,
  LegendRow,
  SHAPE,
} from "@/components/tools/geometry/diagram";

/*
 * The circle figure: the radius, the central angle, the sector, the chord and the
 * circular segment it cuts off, with the arc length, chord and radius labeled.
 */

export interface CircleDiagramProps {
  title: string;
  svgRef: RefObject<SVGSVGElement | null>;
  angleDegrees: number;
  labels: { radius: string; angle: string; arc: string; chord: string };
}

const CX = 100;
const CY = 118;
const R = 82;
const LEGEND_X = 206;

export function CircleDiagram({
  title,
  svgRef,
  angleDegrees,
  labels,
}: CircleDiagramProps) {
  const theta = clamp(angleDegrees, 0.5, 359.5);
  const rad = (theta * Math.PI) / 180;
  const big = theta > 180 ? 1 : 0;
  const start = { x: CX + R, y: CY };
  const end = { x: CX + R * Math.cos(rad), y: CY - R * Math.sin(rad) };
  const mid = rad / 2;
  const outside = (radius: number) => ({
    x: CX + radius * Math.cos(mid),
    y: CY - radius * Math.sin(mid) + 4,
  });
  const arcSpot = outside(R + 12);
  // The chord's letter sits a third of the way along it, on the side of the cap.
  const chordLength = Math.hypot(end.x - start.x, end.y - start.y) || 1;
  const along = {
    x: (end.x - start.x) / chordLength,
    y: (end.y - start.y) / chordLength,
  };
  const chordMid = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
  let normal = { x: along.y, y: -along.x };
  const away = normal.x * (chordMid.x - CX) + normal.y * (chordMid.y - CY);
  if (away < -1e-6 || (Math.abs(away) <= 1e-6 && normal.y > 0)) {
    normal = { x: -normal.x, y: -normal.y };
  }
  const chordSpot = {
    x: start.x + along.x * chordLength * 0.3 + normal.x * 11,
    y: start.y + along.y * chordLength * 0.3 + normal.y * 11 + 4,
  };
  const small = 20;
  const arcEnd = {
    x: CX + small * Math.cos(rad),
    y: CY - small * Math.sin(rad),
  };
  const angleSpot = outside(small + 12);
  return (
    <DiagramFrame svgRef={svgRef} title={title}>
      <circle
        cx={CX}
        cy={CY}
        fill="none"
        r={R}
        stroke={SHAPE.stroke}
        strokeWidth={2.2}
      />
      <path
        d={`M${CX} ${CY} L${start.x} ${start.y} A${R} ${R} 0 ${big} 0 ${end.x} ${end.y} Z`}
        fill={SHAPE.fill}
        fillOpacity={0.14}
      />
      <path
        d={`M${start.x} ${start.y} A${R} ${R} 0 ${big} 0 ${end.x} ${end.y} Z`}
        fill={SHAPE.accent}
        fillOpacity={0.3}
      />
      <path
        d={`M${start.x} ${start.y} A${R} ${R} 0 ${big} 0 ${end.x} ${end.y}`}
        fill="none"
        stroke={SHAPE.stroke}
        strokeWidth={4.5}
      />
      <Guide x1={CX} x2={start.x} y1={CY} y2={start.y} />
      <Guide x1={CX} x2={end.x} y1={CY} y2={end.y} />
      <line
        stroke={SHAPE.ink}
        strokeWidth={2}
        x1={start.x}
        x2={end.x}
        y1={start.y}
        y2={end.y}
      />
      <path
        d={`M${CX + small} ${CY} A${small} ${small} 0 ${big} 0 ${arcEnd.x} ${arcEnd.y}`}
        fill="none"
        stroke={SHAPE.guide}
        strokeWidth={1.5}
      />
      <circle cx={CX} cy={CY} fill={SHAPE.guide} r={3} />
      <Label italic x={CX + R / 2} y={CY + 17}>
        r
      </Label>
      <Label
        anchor={Math.cos(mid) >= 0 ? "start" : "end"}
        color={SHAPE.guide}
        italic
        x={angleSpot.x}
        y={angleSpot.y}
      >
        θ
      </Label>
      <Label
        anchor={Math.cos(mid) >= 0 ? "start" : "end"}
        italic
        x={arcSpot.x}
        y={arcSpot.y}
      >
        s
      </Label>
      <Label anchor="middle" italic x={chordSpot.x} y={chordSpot.y}>
        c
      </Label>
      <LegendRow
        sample={
          <line
            stroke={SHAPE.guide}
            strokeDasharray="5 4"
            strokeWidth={2}
            x1={0}
            x2={20}
            y1={0}
            y2={0}
          />
        }
        text={labels.radius}
        x={LEGEND_X}
        y={70}
      />
      <LegendRow
        sample={
          <path
            d="M0 5 A12 12 0 0 1 20 -3"
            fill="none"
            stroke={SHAPE.guide}
            strokeWidth={1.5}
          />
        }
        text={labels.angle}
        x={LEGEND_X}
        y={100}
      />
      <LegendRow
        sample={
          <line
            stroke={SHAPE.stroke}
            strokeWidth={4.5}
            x1={0}
            x2={20}
            y1={0}
            y2={0}
          />
        }
        text={labels.arc}
        x={LEGEND_X}
        y={130}
      />
      <LegendRow
        sample={
          <line
            stroke={SHAPE.ink}
            strokeWidth={2}
            x1={0}
            x2={20}
            y1={0}
            y2={0}
          />
        }
        text={labels.chord}
        x={LEGEND_X}
        y={160}
      />
    </DiagramFrame>
  );
}
